/**
 * What value a PARTY offers for one form input (MAR-176, shared by MAR-179).
 *
 * Extracted from `EntityPartyFill.vue` when batch mode needed the same answer for a whole table:
 * every row of a batch resolves to an entity, and its autofillable columns must fill with exactly
 * what a single run would have written. Two implementations of "what does this party put in this
 * blank" would eventually disagree, and the disagreement would show up as a batch of thirty
 * documents differing from the one the reviewer checked by hand.
 *
 * Pure and free of Vue on purpose — the run form, the batch table and the tests all read it.
 */

import { isDerivedAttributeKey, isDerivedFact, resolveDerivedFact } from './derivedFacts'
import { canonicalAttributeKey, type CanonicalAttributeKey } from './attributeKeys'
import { idTypeForProperty, type EntityIdType } from '../vocabulary/crossService'
import { optionLabel, optionValue } from '../workflows/fieldOptions'

/** The half of an entity candidate a fill actually reads. */
export interface PartyFacts {
  name?: string | null
  idType?: EntityIdType | string | null
  idNumber?: string | null
  attributes?: Partial<Record<CanonicalAttributeKey, string>> | Record<string, string | undefined> | null
}

/** One input a fill can write: which fact it wants, and the values it will accept. */
export interface PartyFillTargetShape {
  field: string
  idType?: string | null
  options?: unknown[] | null
}

function normalizeFactProperty(rawField: string): string {
  let prop = rawField.trim()
  const dotIdx = prop.lastIndexOf('.')
  if (dotIdx !== -1) prop = prop.slice(dotIdx + 1).trim()
  if (prop.startsWith('attribute:')) prop = prop.slice('attribute:'.length).trim()
  else if (prop.startsWith('derived:')) prop = prop.slice('derived:'.length).trim()
  if (prop.startsWith('idNumber:')) prop = prop.slice('idNumber:'.length).trim()
  if (isDerivedAttributeKey(prop)) return 'age'
  return prop
}

/**
 * The raw value an entity candidate offers for one fact, or "" when it does not carry that fact.
 *
 * Accepts flat property keys (`name`, `given_names`, `national_id`, `tax_id`, `age`,
 * `registry_number`, `plate`), `<slot>.<property>` paths (`trabajador.given_names`,
 * `inmueble.registry_number`), and legacy `attribute:<key>` / `derived:<key>` / `idNumber` keys
 * (MAR-1068, MAR-1104). `given_names` and `surnames` resolve strictly from stored attributes — a
 * composed `name` is never split heuristically.
 */
export function rawValueForFact(party: PartyFacts, field: string, referenceDate?: Date): string {
  const prop = normalizeFactProperty(field)
  if (!prop) return ''
  if (prop === 'name') return party.name ?? ''
  if (prop === 'id_number' || prop === 'idNumber') return party.idNumber ?? ''

  const attrs = party.attributes as Record<string, string | undefined> | null | undefined

  const wantedIdType = idTypeForProperty(prop) ?? (prop === 'registration_number' ? 'business_registration' : null)
  if (wantedIdType) {
    const directAttr = attrs?.[prop]
      ?? (prop === 'resident_card' ? attrs?.foreigner_id : undefined)
      ?? (prop === 'foreigner_id' ? attrs?.resident_card : undefined)
      ?? (prop === 'business_registration' ? attrs?.registration_number : undefined)
      ?? (prop === 'registration_number' ? attrs?.business_registration : undefined)
    if (directAttr) return directAttr
    const partyIdType = party.idType ? (idTypeForProperty(party.idType) ?? party.idType) : null
    if (!partyIdType || partyIdType === wantedIdType) {
      return party.idNumber ?? ''
    }
    return ''
  }

  // Computed, not stored: an age is only true on the day it is written, so it is derived from the
  // party's date of birth when the form is filled rather than persisted onto the entity.
  if (isDerivedFact(prop)) {
    return resolveDerivedFact(prop, party.attributes, referenceDate)
  }
  const canonical = canonicalAttributeKey(prop)
  if (canonical) {
    return attrs?.[canonical] ?? attrs?.[prop] ?? ''
  }
  return attrs?.[prop] ?? ''
}

/** Strip accents, case, and punctuation so `SOLTERO(A)` and `soltero/a` compare equal. */
export function foldValue(raw: string): string {
  return raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
}

/**
 * The value to actually write into one input.
 *
 * For a plain text input that is the stored value verbatim. For a **select** it must be one of the
 * declared option values, so the document's wording is matched onto an option and the raw string is
 * never written: `SOLTERO(A)` on an `estado_civil` select whose options are `soltero/a`, `casado/a`
 * … would otherwise leave a control that matches no option — rendering blank while the run form's
 * `fieldComplete` counts it filled, i.e. an empty required field walking past the run gate.
 *
 * No match returns "", which callers treat as "this party does not have that fact" and leave the
 * input untouched. Guessing at an unmatched marital status is exactly the silent wrongness this
 * feature must not introduce.
 */
export function valueForTarget(party: PartyFacts, target: PartyFillTargetShape, referenceDate?: Date): string {
  const normField = normalizeFactProperty(target.field)
  const raw = (normField === 'id_number' || normField === 'idNumber') && target.idType
    ? (rawValueForFact(party, target.idType, referenceDate) || rawValueForFact(party, target.field, referenceDate))
    : rawValueForFact(party, target.field, referenceDate)
  if (!raw || !target.options?.length) return raw
  const wanted = foldValue(raw)
  for (const opt of target.options) {
    const value = optionValue(opt)
    const optLabel = optionLabel(opt)
    if (typeof value !== 'string') continue
    if (foldValue(value) === wanted || (typeof optLabel === 'string' && foldValue(optLabel) === wanted)) {
      return value
    }
  }
  return ''
}

/**
 * Fill a row's blanks from a party, recording WHICH keys were filled (MAR-179).
 *
 * The recording is the point. Without it there is no way to tell a value we inferred from the
 * matched party apart from one the user typed, and the two must be treated differently the moment
 * the row's identity changes — see `stripPartyFacts`.
 *
 * Never overwrites a non-empty cell: the user's typing, and an imported sheet's values, both outrank
 * what we know. A sheet saying this employee's address changed is the newer fact.
 */
export function applyPartyFacts(
  values: Record<string, string>,
  party: PartyFacts,
  columns: Array<{ key: string, field?: string, options?: unknown[] | null }>,
  referenceDate?: Date,
): { values: Record<string, string>, autofilled: string[] } {
  const next = { ...values }
  const autofilled: string[] = []
  for (const col of columns) {
    if (!col.field || next[col.key]?.trim()) continue
    const value = valueForTarget(party, { field: col.field, options: col.options }, referenceDate)
    if (value) {
      next[col.key] = value
      autofilled.push(col.key)
    }
  }
  return { values: next, autofilled }
}

/**
 * Drop the facts that came from a party this row no longer claims to be (MAR-179).
 *
 * Correcting a mistyped identity number used to invalidate the match while LEAVING the previous
 * party's name, address and age in the row — in columns hidden by default, so a run produced a
 * document carrying one person's identity number beside another person's name, with nothing on
 * screen to contradict it. Found by running an actual batch.
 *
 * Removes only the recorded keys, and never the identity column itself: what the user supplied has
 * to survive, and the number they just typed is the whole reason we are re-resolving.
 */
export function stripPartyFacts(
  values: Record<string, string>,
  autofilled: string[] | undefined,
  identityKey: string,
): Record<string, string> {
  if (!autofilled?.length) return values
  const next = { ...values }
  for (const key of autofilled) {
    if (key !== identityKey) delete next[key]
  }
  return next
}
